import { useMemo } from 'react';
import { useAppStore } from '../store';
import { formatMonthShortFromString } from '../utils/format';

export interface MonthlySpending {
  month: string;
  total: number;
  [categoryId: string]: number | string;
}

export interface SpendingTrendsOptions {
  months?: number;
  categories?: string[];
}

export { formatMonthShortFromString as formatMonth };

function getLastNMonths(n: number): string[] {
  const months: string[] = [];
  const now = new Date();

  for (let i = n - 1; i >= 0; i--) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    months.push(`${year}-${month}`);
  }

  return months;
}

export function useSpendingTrends(options: SpendingTrendsOptions = {}) {
  const { months = 6, categories } = options;
  const transactions = useAppStore((state) => state.transactions);
  const allCategories = useAppStore((state) => state.categories);

  const data = useMemo(() => {
    const lastMonths = getLastNMonths(months);
    const categorySet = categories ? new Set(categories) : null;

    const monthlyData: Record<string, MonthlySpending> = {};

    lastMonths.forEach((month) => {
      monthlyData[month] = { month, total: 0 };
    });

    transactions.forEach((transaction) => {
      if (transaction.type !== 'expense') return;
      if (transaction.excludedFromBudget) return;

      const transactionMonth = transaction.date.slice(0, 7);
      if (!monthlyData[transactionMonth]) return;

      const amount = Math.abs(transaction.amount);
      monthlyData[transactionMonth].total += amount;

      if (transaction.categoryId) {
        if (!categorySet || categorySet.has(transaction.categoryId)) {
          const currentValue =
            (monthlyData[transactionMonth][transaction.categoryId] as number) ||
            0;
          monthlyData[transactionMonth][transaction.categoryId] =
            currentValue + amount;
        }
      }
    });

    return lastMonths.map((month) => monthlyData[month]);
  }, [transactions, months, categories]);

  const availableCategories = useMemo(() => {
    const categoryIds = new Set<string>();

    data.forEach((monthData) => {
      Object.keys(monthData).forEach((key) => {
        if (key !== 'month' && key !== 'total' && monthData[key]) {
          categoryIds.add(key);
        }
      });
    });

    return allCategories.filter((cat) => categoryIds.has(cat.id));
  }, [data, allCategories]);

  return { data, availableCategories };
}
